import React, { memo, useCallback } from 'react'
import {
  NavBar, Link, DraftsIcon, RecentIcon, ArrowDownIcon, ArrowUpIcon, Dropdown,
  DropdownLink, CogIcon
} from '@miroculus/nucleo'
import { ACCOUNT_SETTINGS_URL, DRAFTS_URL, LOGOUT_URL, RECENT_URL, organizationUrl, teamUrl } from 'cons/routes'
import PropTypes from 'prop-types'
import classnames from 'classnames/bind'
import { connect } from 'react-redux'
import { useRouteMatch, matchPath, useLocation } from 'react-router-dom'
import Collapsible from '../Collapsible/Collapsible'
import {
  getLoadingOrganizations
} from 'reduxModules/organizations/selectors'
import { getLoading } from 'reduxModules/auth/selectors'
import { userPlaceholder } from 'images'
import styles from './Sidebar.scss'

const cx = classnames.bind(styles)

const teamsPropType = PropTypes.arrayOf(PropTypes.shape({
  id: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  permissions: PropTypes.shape({
    canCreateProtocol: PropTypes.bool,
    canEditProtocol: PropTypes.bool,
    canEditWorkspace: PropTypes.bool,
    canSendProtocol: PropTypes.bool
  })
}))

const UserAccountDropdownActions = () => (
  <Dropdown
    button={<ArrowDownIcon />}
    align='right'
  >
    <DropdownLink to={ACCOUNT_SETTINGS_URL}>Account Settings</DropdownLink>
    <DropdownLink to={LOGOUT_URL}>Logout</DropdownLink>
  </Dropdown>
)

UserAccountDropdownActions.propTypes = {}

const UserAccountBrief = ({
  userName,
  email
}) => {
  return (
    <div className={cx('userAccountBrief')} data-testid='userAccountBrief'>
      <div className={cx('userAccountBriefAvatar')}>
        <div className={cx('userAccountBriefAvatarImage')}>
          <img src={userPlaceholder} alt='Avatar' />
        </div>
      </div>
      <div className={cx('userAccountBriefInfo')}>
        <p data-testid='navBarUserName' className={cx('userAccountBriefInfoUserName')}>{userName}</p>
        <p data-testid='navBarUserEmail' className={cx('userAccountBriefInfoEmail')}>{email}</p>
      </div>
      <UserAccountDropdownActions />
    </div>
  )
}

UserAccountBrief.propTypes = {
  userName: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired
}

const OrganizationTeams = ({
  teams
}) => {
  return (
    <ul className={cx('organizationTeamsList')}>
      {teams.map(({ id: teamId, title }) =>
        <li className={cx({ selected: useRouteMatch(teamUrl(teamId)) })} key={teamId}>
          <Link
            to={teamUrl(teamId)}
            aria-current={useRouteMatch(teamUrl(teamId))?.isExact}
            data-testid={`team-button-${title}`}
          >
            {title}
          </Link>
        </li>
      )}
    </ul>
  )
}

OrganizationTeams.propTypes = {
  teams: teamsPropType.isRequired
}

/**
 * Render the app Sidebar with Recent view and Drafts view links and also lists User Organizations and its Teams
 * The header of the Sidebar contains some User information and actions for logout and show the Account Settings modal
 */
export const Sidebar = memo(({
  organizations,
  userName,
  email,
  loadingOrganizations,
  loading
}) => {
  const recentMatch = useRouteMatch(RECENT_URL)
  const rootMatch = useRouteMatch('/')
  const draftsMatch = useRouteMatch(DRAFTS_URL)
  const { pathname } = useLocation()

  const renderShortcutList = useCallback(() => (
    <ul className={cx('shortcutsList', 'navBarBlock')}>
      <li className={cx({ selected: !!recentMatch || rootMatch?.isExact })}>
        <Link to={RECENT_URL} aria-current={!!recentMatch || rootMatch?.isExact}>
          <RecentIcon />Recent
        </Link>
      </li>
      <li className={cx({ selected: !!draftsMatch })}>
        <Link to={DRAFTS_URL} aria-current={!!draftsMatch}>
          <DraftsIcon />Drafts
        </Link>
      </li>
    </ul>
  ), [recentMatch, rootMatch, draftsMatch])

  const renderOrganizations = useCallback(() => (
    <ul className={cx('organizationsList')}>
      {!loadingOrganizations && organizations?.filter(({ name }) => !!name).map(({ name, teams, slug }) => {
        const organizationName = name ?? 'No Organization'

        const match = matchPath(pathname, {
          path: organizationUrl(':orgSlug'),
          exact: true,
          strict: false
        })

        const selected = match?.params?.orgSlug === slug

        return (
          <li className={cx('organizationsListItem', 'navBarBlock')} key={organizationName} data-testid={`organization-link-${organizationName}`}>
            <Link className={cx('organizationsListItemLink', { selected })} aria-current={selected} to={organizationUrl(slug)} data-testid={`sidebar-org-${slug}`}>{organizationName}</Link>
            <Collapsible
              header={teams?.length ? (expanded) => (expanded ? <ArrowDownIcon /> : <ArrowUpIcon />) : () => null}
            >
              <OrganizationTeams teams={teams} />
            </Collapsible>
          </li>
        )
      })}
    </ul>
  ), [organizations, loadingOrganizations, pathname])

  const showUserWithoutOrganizations = !loading && !loadingOrganizations && !organizations?.length

  return (
    <div className={cx('container')} data-testid='sidebar'>
      <NavBar>
        <UserAccountBrief userName={userName} email={email} />
        <div className={cx('navBarActions')}>
          {renderShortcutList()}
          {renderOrganizations()}
          {showUserWithoutOrganizations &&
            <div className={cx('organizationsListEmpty')}>
              <CogIcon />
              <p>No organization has been added yet.</p>
            </div>}
        </div>
      </NavBar>
    </div>

  )
})

Sidebar.propTypes = {
  organizations: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    teams: teamsPropType.isRequired
  })),
  userName: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  loadingOrganizations: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired
}

const mapStateToProps = (state) => ({
  userName: state.auth.user.name,
  email: state.auth.user.email,
  loadingOrganizations: getLoadingOrganizations(state),
  loading: getLoading(state)
})

export default connect(
  mapStateToProps
)(Sidebar)
